import type { PressEvents } from '@react-native-aria/interactions'
import { Children, cloneElement, useMemo, type ReactElement, type ReactNode } from 'react'
import { Pressable } from 'react-native'

import { TEST_ID } from '@/src/general/constants/testId'
import { useTranslate } from '@/src/lang/useTranslate'

import { Button, type TDefaultButtonProps } from '../button/default'
import { Listbox, type TListboxItemProps } from '../listbox'
import { Modal } from '../modal/Modal'
import { useActionSheet } from './useActionSheet'

export type TActionSheetProps = {
  items: TListboxItemProps[]
  children:
    | ReactElement<TAnyProps & Pick<TDefaultButtonProps, 'onPress'>>
    | ((props: { onPress: PressEvents['onPress'] }) => ReactElement)
}

export const ActionSheetMobile = ({ children, items }: TActionSheetProps): ReactNode => {
  const { t } = useTranslate()
  const { isActionSheetVisible, setIsActionSheetVisible, renderActionItem } = useActionSheet()

  // Force a single child
  const child = useMemo(() => {
    if (!children) throw new Error('ActionSheetMobile should have a button child')

    const handlePress: TDefaultButtonProps['onPress'] = (e) => {
      e?.persist()
      e?.preventDefault?.()
      e?.stopPropagation?.()
      setIsActionSheetVisible(() => true)
    }

    if (typeof children === 'function') return children({ onPress: handlePress })

    if (typeof children !== 'object') return <Pressable onPress={handlePress}>{children}</Pressable>

    return cloneElement(Children.only(children), { onPress: handlePress })
  }, [children, setIsActionSheetVisible])

  return (
    <>
      {child}
      <Modal
        testID={TEST_ID.MISCELLANEOUS.ACTION_SHEET_MODAL}
        showModal={isActionSheetVisible}
        onCancel={() => setIsActionSheetVisible(false)}
        classNames={{
          header: 'min-h-0 h-0',
          base: 'shadow-none justify-start bg-transparent',
          body: 'gap-8 overflow-y-visible px-4 justify-start',
          handle: 'bg-white',
        }}
        showHandle={false}
        backgroundStyle={{ backgroundColor: 'transparent' }}
        style={{ shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0, shadowRadius: 0 }}
      >
        <Listbox className="shadow-md rounded-lg p-2 bg-white">
          {items.map((item, index) => renderActionItem(item, index))}
        </Listbox>
        <Button
          variant="solid"
          colors="default"
          className="shadow-md w-full"
          onPress={(e) => {
            e?.preventDefault?.()
            e?.stopPropagation?.()
            setIsActionSheetVisible(false)
          }}
          testID={TEST_ID.ACTION_SHEET.CANCEL_BUTTON}
        >
          {t('word.cancel')}
        </Button>
      </Modal>
    </>
  )
}
